import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { errorMessage } from '../i18n/errorMessages';
import { RemoteUIProvider } from '../i18n/remote';
import { AgentComposer, type AgentComposerProps } from '@borgee/agent-remote-web/react';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  PaperClipOutlined,
  CloseCircleFilled,
  SmileOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import {
  useAuthStore,
  useChannelStore,
  useDmStore,
  useMessageStore,
  useWsStore,
} from '../stores';
import SlashCommandPicker from './SlashCommandPicker';
import Toolbar from './Toolbar';
import { useSlashCommands } from '../hooks/useSlashCommands';
import { useComposerDraft } from '../hooks/useComposerDraft';
import { attachmentDraftKey, useNormalizedConversationStore } from '../stores/useNormalizedConversationStore';
import { useRecentInputHistory } from '../hooks/useRecentInputHistory';
import { useChannelMembers } from '../hooks/useChannelMembers';
import type { RecentInputEntry } from '../hooks/useRecentInputHistory';
import { registerDismissibleLayer, useDismissibleLayer } from '../hooks/useDismiss';
import { formatFileSize } from '../i18n/fileSize';
import styles from './MessageInput.module.css';
import { createMentionExtension } from '../extensions/mention';
import { commandRegistry } from '../commands/registry';
import type { CommandDefinition, RemoteCommand, CommandContext } from '../commands/registry';
import { isRemoteCommand } from './SlashCommandPicker';
import '../commands/builtins';
import * as api from '../lib/api';
import { toUploadFile } from '../hosts/web/files';
import { onMessageAcked, dispatchOutgoingMessage } from '../stores/actions';
import { randomUUID } from '../lib/uuid';
import {
  appendMessageAttachments,
  isPreviewableImageFile,
  MESSAGE_ATTACHMENT_ACCEPT,
  type AttachmentSelectionError,
} from '../lib/message-attachments';
import type { ChannelMember } from '../lib/api';
import type { SendStatus } from '../types';
import { ensureRecipientMention } from '../lib/recipient-message';

interface Props {
  normalizedComposer?: Pick<AgentComposerProps, 'state' | 'onCancel' | 'unavailableMessage' | 'sessionKey' | 'sessionControls' | 'hideSessionSettings' | 'onSetSessionSetting' | 'onListCommands' | 'onExecuteCommand' | 'onSteerMessage' | 'onQueueMessage' | 'sendMessageHint'>;
  recipientAgent?: { id: string; name: string };
  channelId: string;
  /** Names the draft this composer persists its unsent text under; see `useComposerDraft`. */
  draftKey: string;
  /**
   * Takes over sending. It must reject when the message neither reached the
   * wire nor became a pending row the member can retry from, so the composer
   * can keep the text and its draft instead of clearing them.
   */
  onSendOverride?: (content: string, mentions: string[], attachments: File[]) => Promise<void>;
  /** A contextual starter may populate this composer once; user edits win afterwards. */
  suggestedText?: string;
  onSuggestedTextApplied?: () => void;
  /** Read-only conversations retain history but must not accept outbound input. */
  disabled?: boolean;
}

function resolveRecentInputScopeKey(
  draftKey: string,
  channelId: string,
  channelType: 'dm' | 'channel',
): string {
  if (draftKey.startsWith('thread:')) return draftKey;
  if (draftKey.startsWith('pending-thread:')) return draftKey;
  if (draftKey.startsWith('channel:')) {
    return `${channelType}:${channelId}`;
  }
  return draftKey;
}

function getMarkdownFromEditor(ed: { storage: unknown; getText: () => string }): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mdStorage = (ed.storage as any).markdown as { getMarkdown(): string } | undefined;
  return mdStorage?.getMarkdown() ?? ed.getText();
}

function extractMentionIds(ed: {
  getJSON: () => { content?: Array<Record<string, unknown>> };
}): string[] {
  const ids: string[] = [];
  const walk = (node: Record<string, unknown>) => {
    if (
      node.type === 'mention' &&
      typeof (node.attrs as Record<string, unknown>)?.id === 'string'
    ) {
      ids.push((node.attrs as Record<string, string>).id);
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) walk(child as Record<string, unknown>);
    }
  };
  const json = ed.getJSON();
  if (json.content) {
    for (const node of json.content) walk(node as Record<string, unknown>);
  }
  return [...new Set(ids)];
}

function attachmentSelectionErrorMessage(
  error: AttachmentSelectionError,
  filename?: string,
): MessageDescriptor {
  switch (error) {
    case 'unsafe-name':
      return msg`The attachment filename is not allowed: ${filename ?? ''}`;
    case 'unsupported-type':
      return msg`This attachment type is not supported: ${filename ?? ''}`;
    case 'mime-mismatch':
      return msg`The attachment type does not match its extension: ${filename ?? ''}`;
    case 'file-too-large':
      return msg`Each attachment must be 10 MB or smaller`;
    case 'too-many-files':
      return msg`A message can contain at most 10 attachments`;
    case 'batch-too-large':
      return msg`Attachments can total at most 50 MB`;
  }
}

export default function MessageInput({
  channelId,
  draftKey,
  onSendOverride,
  suggestedText,
  onSuggestedTextApplied,
  disabled = false,
  normalizedComposer,
  recipientAgent,
}: Props) {
  const { t, i18n } = useLingui();
  const currentUser = useAuthStore((s) => s.currentUser);
  const channels = useChannelStore((s) => s.channels);
  const dmChannels = useDmStore((s) => s.dmChannels);
  const channel = channels.find((c) => c.id === channelId);
  const dmChannel = dmChannels.find((c) => c.id === channelId);
  const channelType: 'dm' | 'channel' = dmChannel || channel?.type === 'dm' ? 'dm' : 'channel';
  const sendWsMessage = useWsStore((s) => s.sendWsMessage);
  const addPendingMessage = useMessageStore((s) => s.addPendingMessage);
  const failPendingMessage = useMessageStore((s) => s.failPendingMessage);
  const [text, setText] = useState('');
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const normalizedDraft = useRef({ revision: 0, mounted: true, sessionKey: normalizedComposer?.sessionKey });
  normalizedDraft.current.sessionKey = normalizedComposer?.sessionKey;
  useEffect(() => {
    normalizedDraft.current.mounted = true;
    return () => { normalizedDraft.current.mounted = false; };
  }, []);
  useEffect(() => { setSendStatus('idle'); }, [normalizedComposer?.sessionKey]);
  const [commandError, setCommandError] = useState<MessageDescriptor | null>(null);
  const [attachmentError, setAttachmentError] = useState<MessageDescriptor | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const trackAttachments = useNormalizedConversationStore(state => state.trackAttachments);
  useEffect(() => {
    const key = attachmentDraftKey(currentUser?.id ?? '', draftKey);
    trackAttachments(key, pendingFiles.length);
    return () => trackAttachments(key, 0);
  }, [currentUser?.id, draftKey, pendingFiles.length, trackAttachments]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastTypingSent = useRef(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const {
    restore: restoreDraft,
    record: recordDraft,
    clear: clearDraft,
  } = useComposerDraft(draftKey);
  const { recordSentInput, recallOlderInput, recallNewerInput, clearRecallState, hasActiveRecall } =
    useRecentInputHistory(resolveRecentInputScopeKey(draftKey, channelId, channelType));

  // Browser object URLs are created only for safe image elements. Other
  // attachments stay metadata-only until the server returns a download URL.
  const previewUrls = useMemo(
    () => pendingFiles.map((file) => (
      isPreviewableImageFile(file) ? URL.createObjectURL(file) : null
    )),
    [pendingFiles],
  );
  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  const [slashResolvedUser, setSlashResolvedUser] = useState<
    { id: string; username: string } | undefined
  >();

  // The picker answers what the member is typing now. Text hydrated from a
  // draft was written in an earlier sitting and does not open it; the next edit
  // does.
  const [editedSinceHydration, setEditedSinceHydration] = useState(false);

  const slash = useSlashCommands(text, editedSinceHydration);

  const { members: mentionMembers } = useChannelMembers(channelId);

  const mentionUsersRef = useRef<ChannelMember[]>(mentionMembers);
  mentionUsersRef.current = mentionMembers;

  const mentionActiveRef = useRef(false);

  // Refs keep `createMentionExtension`'s identity stable for the suggestion
  // render closure while still tracking the channel the composer is currently
  // rendered in: a DM's mention popover has its own empty-state copy, since its
  // candidate set is capped at the two members and cannot be widened.
  const channelTypeRef = useRef<'dm' | 'channel'>(channelType);
  channelTypeRef.current = channelType;

  const mentionExtension = useMemo(
    () =>
      createMentionExtension(
        () => mentionUsersRef.current,
        mentionActiveRef,
        () => channelTypeRef.current,
      ),
    [],
  );

  const placeholder = t`Write a message… (Enter to send, Ctrl+Enter for a new line)`;
  const queueAttachments = useCallback(
    (files: Iterable<File>) => {
      if (disabled) return false;
      const additions = Array.from(files);
      if (additions.length === 0) return false;
      const result = appendMessageAttachments(pendingFiles, additions);
      if (!result.ok) {
        setAttachmentError(attachmentSelectionErrorMessage(result.error, result.filename));
        return false;
      }
      setPendingFiles(result.files);
      setAttachmentError(null);
      return true;
    },
    [disabled, pendingFiles],
  );
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Markdown.configure({
        html: false,
        transformCopiedText: true,
        transformPastedText: true,
      }),
      mentionExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        'data-placeholder': placeholder,
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          event.preventDefault();
          if (!normalizedComposer) queueAttachments(files);
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        const pastedFiles: File[] = [];
        if (items) {
          for (let i = 0; i < items.length; i++) {
            const item = items[i]!;
            if (item.kind === 'file') {
              const file = item.getAsFile();
              if (file) pastedFiles.push(file);
            }
          }
        }
        if (pastedFiles.length === 0 && event.clipboardData?.files) {
          for (let i = 0; i < event.clipboardData.files.length; i++) {
            const file = event.clipboardData.files[i];
            if (file) pastedFiles.push(file);
          }
        }
        if (pastedFiles.length > 0) {
          event.preventDefault();
          if (!normalizedComposer) queueAttachments(pastedFiles);
          return true;
        }
        return false;
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          if (event.ctrlKey || event.metaKey) {
            return false;
          }
          if (mentionActiveRef.current) return false;
          event.preventDefault();
          handleSendRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const newText = getMarkdownFromEditor(ed);
      setText(newText);
      recordDraft(ed.isEmpty ? null : ed.getJSON());
      clearRecallState();
      setEditedSinceHydration(true);
      // A failure banner describes the send that was attempted, not the message
      // the member is now writing in its place.
      setSendStatus((status) => (status === 'error' ? 'idle' : status));
      emitTyping();

      if (slashResolvedUser) {
        setSlashResolvedUser(undefined);
      }
    },
  });

  // Names the surface whose text the box is showing. A send is issued against
  // one surface and may return against another, so it is this, and not the key
  // the send was started with, that says whether the box on screen is still the
  // one that send is entitled to touch.
  const shownDraftKeyRef = useRef(draftKey);

  // Fills the composer from the stored draft whenever it takes on a new key —
  // a channel switch, or a thread panel swapping surfaces. `restoreDraft`
  // changes identity only with the key, so this never fights live typing, and
  // an empty draft deliberately empties the box: the previous key's text must
  // not follow the member into the next one.
  useEffect(() => {
    // Read-only state is not a document edit. Emitting an update here can queue
    // an empty draft during StrictMode's mount replay before hydration runs.
    editor?.setEditable(!disabled, false);
  }, [editor, disabled]);

  useEffect(() => {
    if (!suggestedText || !editor || editor.isDestroyed) return;
    editor.commands.setContent(suggestedText, { emitUpdate: false });
    setText(suggestedText);
    setEditedSinceHydration(true);
    editor.commands.focus('end');
    onSuggestedTextApplied?.();
  }, [editor, suggestedText, onSuggestedTextApplied]);

  useEffect(() => {
    shownDraftKeyRef.current = draftKey;
    if (!editor || editor.isDestroyed) return;
    const doc = restoreDraft();
    editor.commands.setContent(doc ?? '', { emitUpdate: false });
    setText(doc === null ? '' : normalizedComposer && typeof doc.attrs?.composerMarkdown === 'string' ? doc.attrs.composerMarkdown : getMarkdownFromEditor(editor));
    clearRecallState();
    setEditedSinceHydration(false);
    // A failure reported under the composer describes the send that was
    // attempted on the outgoing surface; neither banner follows it to the next.
    setSendStatus('idle');
    setCommandError(null);
    // Replacing the document puts the caret back at the start, which is worth
    // correcting only for a member who is already typing here. A composer that
    // does not hold focus must not take it: this effect also runs on a cold
    // boot and on every surface the composer is swapped onto, and stealing
    // focus there yanks the caret out of whatever is actually in use and pops
    // the soft keyboard on mobile.
    if (editor.isFocused) editor.commands.focus('end');
  }, [editor, draftKey, restoreDraft, clearRecallState]);

  useEffect(() => {
    setPendingFiles([]);
    setAttachmentError(null);
  }, [draftKey]);

  const handleSendRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!commandError) return;
    const timer = setTimeout(() => setCommandError(null), 4000);
    return () => clearTimeout(timer);
  }, [commandError]);

  const executeCommand = useCallback(
    async (name: string, args: string, resolvedUser?: { id: string; username: string }) => {
      const cmd = commandRegistry.get(name);
      if (!cmd) return false;
      const ctx: CommandContext = {
        i18n,
        channelId,
        currentUserId: currentUser?.id ?? '',
        args,
        resolvedUser,
        api,
      };
      await cmd.execute(ctx);
      return true;
    },
    [channelId, currentUser, i18n],
  );

  // A send that awaits a round trip outlives the surface it was issued on: a
  // channel switch swaps the box's contents, `/leave` and `/dm` navigate away,
  // and a confirmed thread creation replaces the panel. What such a send does
  // when it returns therefore belongs to the surface it started on, and only
  // reaches the box while that surface is still the one being shown.
  const isShowing = useCallback(
    (sentDraftKey: string) => shownDraftKeyRef.current === sentDraftKey,
    [],
  );

  // Empties the box and its draft together once the message is no longer the
  // composer's to hold. The draft dropped is the one the send was issued
  // under; the box is only emptied while it is still showing that surface,
  // because `clearContent` reports an empty document through `onUpdate`, which
  // would record over — and delete — the draft the composer has since adopted.
  const clearAfterSend = useCallback(
    (sentDraftKey: string) => {
      clearDraft(sentDraftKey);
      clearRecallState();
      if (!isShowing(sentDraftKey)) return;
      setText('');
      if (editor && !editor.isDestroyed) {
        editor.commands.clearContent();
        if (!normalizedComposer) editor.commands.focus();
      }
    },
    [editor, clearDraft, isShowing, clearRecallState, normalizedComposer],
  );

  const applyRecentInput = useCallback(
    (nextInput: RecentInputEntry) => {
      if (!editor || editor.isDestroyed) return;
      if (nextInput.markdown.length === 0) {
        editor.commands.setContent('', { emitUpdate: false });
        setText('');
        recordDraft(null);
        editor.commands.focus();
        return;
      }
      editor.commands.setContent(nextInput.doc ?? nextInput.markdown, { emitUpdate: false });
      setText(nextInput.markdown);
      recordDraft(editor.getJSON());
      editor.commands.focus('end');
    },
    [editor, recordDraft],
  );

  const handleSend = useCallback(async () => {
    if (!editor) return;
    const submittedRevision = normalizedDraft.current.revision;
    const matchesNormalizedDraft = () => normalizedDraft.current.mounted
      && normalizedDraft.current.sessionKey === normalizedComposer?.sessionKey
      && normalizedDraft.current.revision === submittedRevision && isShowing(draftKey);

    const markdown = normalizedComposer ? text : getMarkdownFromEditor(editor);
    const content = markdown.trim();
    if (disabled || (!content && pendingFiles.length === 0) || sendStatus === 'sending') return;
    if (normalizedComposer && pendingFiles.length > 0) throw new Error(t`Attachments cannot be sent to this native session. Send them from Messages or remove them first.`);
    const recentInputDoc = markdown === content ? editor.getJSON() : null;

    setCommandError(null);

    if (content.startsWith('/')) {
      const spaceIdx = content.indexOf(' ');
      const name = spaceIdx === -1 ? content.slice(1) : content.slice(1, spaceIdx);
      const args = spaceIdx === -1 ? '' : content.slice(spaceIdx + 1).trim();
      const cmd = commandRegistry.get(name);
      if (cmd) {
        try {
          await executeCommand(name, args, slashResolvedUser);
          recordSentInput({ markdown: content, doc: recentInputDoc });
          setSlashResolvedUser(undefined);
          clearAfterSend(draftKey);
        } catch (err) {
          if (normalizedComposer) throw new Error(i18n._(errorMessage(err, msg`Command failed`)));
          // A command that failed after the composer left the surface it was
          // typed on has no banner to appear under — that surface's draft still
          // holds the text, and the console is what is left to say why it did
          // not run.
          if (!isShowing(draftKey)) {
            console.error('composer command failed:', err);
            return;
          }
          setCommandError(errorMessage(err, msg`Command failed`));
        }
        return;
      }
    }

    const mentionIds = extractMentionIds(editor);

    // Replace mention nodes in markdown with <@userId> format
    let finalContent = content;
    const json = editor.getJSON();
    const mentionMap = new Map<string, string>();
    const walkForLabels = (node: Record<string, unknown>) => {
      if (node.type === 'mention') {
        const attrs = node.attrs as Record<string, string>;
        if (attrs.id && attrs.label) {
          mentionMap.set(attrs.label, attrs.id);
        }
      }
      if (Array.isArray(node.content)) {
        for (const child of node.content) walkForLabels(child as Record<string, unknown>);
      }
    };
    if (json.content) {
      for (const node of json.content) walkForLabels(node as Record<string, unknown>);
    }
    for (const [label, id] of mentionMap) {
      finalContent = finalContent.replace(new RegExp(`@${escapeRegex(label)}`, 'g'), `<@${id}>`);
    }

    if (normalizedComposer && recipientAgent) {
      finalContent = ensureRecipientMention(finalContent, recipientAgent.id);
      if (!mentionIds.includes(recipientAgent.id)) mentionIds.push(recipientAgent.id);
    }

    if (onSendOverride) {
      setSendStatus('sending');
      try {
        await onSendOverride(finalContent, mentionIds, [...pendingFiles]);
      } catch (err) {
        // Nothing went out, so the text stays in the box and its draft stays in
        // storage; the member retries from where they were.
        console.error('composer send override failed:', err);
        if (isShowing(draftKey) && (!normalizedComposer || matchesNormalizedDraft())) setSendStatus('error');
        if (normalizedComposer) throw err;
        return;
      }
      recordSentInput({ markdown: content, doc: recentInputDoc });
      if (normalizedComposer && !matchesNormalizedDraft()) return;
      if (isShowing(draftKey)) {
        setPendingFiles([]);
        setAttachmentError(null);
        setSendStatus('idle');
      }
      clearAfterSend(draftKey);
      return;
    }

    // An attachment goes up over REST, which answers with the stored message,
    // so this rail reconciles its own optimistic row instead of waiting for an
    // ack frame.
    if (pendingFiles.length > 0) {
      const filesToSend = [...pendingFiles];
      const clientMessageId = randomUUID();
      setSendStatus('sending');
      addPendingMessage({
        clientMessageId,
        channelId,
        content: finalContent,
        contentType: 'text',
        status: 'pending',
        createdAt: Date.now(),
        senderName: currentUser?.display_name ?? 'Unknown',
        senderId: currentUser?.id ?? '',
        mentions: mentionIds,
      });
      try {
        const msg = await api.sendMessageWithAttachments(
          channelId,
          finalContent,
          await Promise.all(filesToSend.map(toUploadFile)),
        );
        onMessageAcked(channelId, clientMessageId, msg);
        recordSentInput({ markdown: content, doc: recentInputDoc });
        if (isShowing(draftKey)) {
          setPendingFiles([]);
          setAttachmentError(null);
          setSendStatus('idle');
        }
        clearAfterSend(draftKey);
      } catch (err) {
        console.error('send with attachments failed:', err);
        failPendingMessage(clientMessageId, channelId);
        if (isShowing(draftKey)) setSendStatus('error');
      }
      return;
    }

    dispatchOutgoingMessage({
      channelId,
      content: finalContent,
      contentType: 'text',
      mentions: mentionIds,
    });
    recordSentInput({ markdown: content, doc: recentInputDoc });
    clearAfterSend(draftKey);
  }, [
    editor,
    draftKey,
    sendStatus,
    channelId,
    currentUser,
    executeCommand,
    slashResolvedUser,
    addPendingMessage,
    failPendingMessage,
    pendingFiles,
    onSendOverride,
    clearAfterSend,
    isShowing,
    recordSentInput,
    disabled,
    normalizedComposer,
    recipientAgent,
    text,
    i18n,
  ]);

  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const handleSlashSelect = useCallback(
    async (cmd: CommandDefinition | RemoteCommand) => {
      if (isRemoteCommand(cmd)) {
        const newText = `/${cmd.name} `;
        setText(newText);
        editor?.commands.setContent(newText);
        slash.close();
        editor?.commands.focus('end');
        return;
      }
      if (cmd.paramType === 'none') {
        const ctx: CommandContext = {
        i18n,
          channelId,
          currentUserId: currentUser?.id ?? '',
          args: '',
          api,
        };
        try {
          await cmd.execute(ctx);
        } catch (err) {
          if (!isShowing(draftKey)) {
            console.error('composer command failed:', err);
          } else {
            setCommandError(errorMessage(err, msg`Command failed`));
          }
        }
        clearAfterSend(draftKey);
        slash.close();
      } else {
        const newText = `/${cmd.name} `;
        setText(newText);
        editor?.commands.setContent(newText);
        slash.close();
        editor?.commands.focus('end');
      }
    },
    [channelId, draftKey, currentUser, slash, editor, clearAfterSend, isShowing, i18n],
  );

  const handleEditorKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (slash.isActive && slash.totalCount > 0) {
        if (slash.handleKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const cmd = slash.selectedItem;
          if (cmd) handleSlashSelect(cmd);
          return;
        }
      }
      if (
        editor &&
        !mentionActiveRef.current &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
        (text.length === 0 || hasActiveRecall())
      ) {
        const nextInput = e.key === 'ArrowUp' ? recallOlderInput(text) : recallNewerInput();
        if (nextInput !== null) {
          e.preventDefault();
          applyRecentInput(nextInput);
          setEditedSinceHydration(false);
          return;
        }
      }
    },
    [
      slash,
      handleSlashSelect,
      editor,
      text,
      recallOlderInput,
      recallNewerInput,
      hasActiveRecall,
      applyRecentInput,
    ],
  );

  const emitTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2000) return;
    lastTypingSent.current = now;
    sendWsMessage({ type: 'typing', channel_id: channelId });
  }, [channelId, sendWsMessage]);

  const insertEmojiAtCursor = useCallback(
    (emoji: string) => {
      editor?.commands.insertContent(emoji);
    },
    [editor],
  );

  const closeEmojiPicker = useCallback(() => setEmojiPickerOpen(false), []);
  useDismissibleLayer({
    active: emojiPickerOpen,
    within: [emojiPickerRef, emojiBtnRef],
    onEscape: closeEmojiPicker,
    onOutsidePress: closeEmojiPicker,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      queueAttachments(files);
    }
    e.target.value = '';
  };

  useEffect(() => {
    editor?.setOptions({ editorProps: { ...editor.options.editorProps, attributes: { class: 'tiptap-editor', 'data-placeholder': placeholder } } });
  }, [editor, placeholder]);

  const statusText = () => {
    switch (sendStatus) {
      case 'sending':
        return t`Sending…`;
      case 'error':
        return t`Send failed`;
      default:
        return null;
    }
  };

  if (normalizedComposer) return <RemoteUIProvider><div className="normalized-session-composer" data-message-input-root>
    {recipientAgent && <div className="normalized-session-recipient">{t`To @${recipientAgent.name}`}</div>}
    <AgentComposer {...normalizedComposer} sessionKey={`${currentUser?.id ?? ''}:${draftKey}:${normalizedComposer.sessionKey ?? ''}`} disabled={disabled}
      registerDismissibleLayer={registerDismissibleLayer}
      hideNativeCommands={!normalizedComposer.onListCommands || !normalizedComposer.onExecuteCommand}
      draft={text}
      consoleCommands={commandRegistry.all().map(command => ({ id: `borgee:${command.name}`, name: command.name, kind: 'command' as const, description: i18n._(command.description), inputHint: command.placeholder ? i18n._(command.placeholder) : undefined }))}
      onExecuteConsoleCommand={async (id, args) => {
        const content = text;
        try {
          await executeCommand(id.slice('borgee:'.length), args.trim(), slashResolvedUser);
          recordSentInput({ markdown: content, doc: null });
          setSlashResolvedUser(undefined);
          return {};
        } catch (error) { throw new Error(i18n._(errorMessage(error, msg`Command failed`))); }
      }}
      onDraftChange={(value) => {
        normalizedDraft.current.revision += 1;
        setText(value);
        editor?.commands.setContent(value, { emitUpdate: false });
        const doc = editor?.getJSON();
        recordDraft(value && doc ? { ...doc, attrs: { ...doc.attrs, composerMarkdown: value } } : null);
      }}
      onSteerMessage={normalizedComposer.onSteerMessage ? async value => {
        if (pendingFiles.length > 0) throw new Error(t`Attachments cannot be sent to this native session. Send them from Messages or remove them first.`);
        await normalizedComposer.onSteerMessage!(value);
        recordSentInput({ markdown: value, doc: null });
      } : undefined}
      onQueueMessage={normalizedComposer.onQueueMessage ? async value => {
        if (pendingFiles.length > 0) throw new Error(t`Attachments cannot be sent to this native session. Send them from Messages or remove them first.`);
        await normalizedComposer.onQueueMessage!(value);
        recordSentInput({ markdown: value, doc: null });
      } : undefined}
      onSendMessage={async () => { await handleSend(); }}
      attachments={pendingFiles.length > 0 ? <div className="normalized-composer-attachments">
        {pendingFiles.map((file, index) => <span key={`${file.name}:${index}`}>
          {previewUrls[index] ? <img src={previewUrls[index]!} alt={file.name} /> : <span>{file.name}</span>}
          <button type="button" aria-label={t`Remove ${file.name}`} onClick={() => setPendingFiles(files => files.filter((_, position) => position !== index))}>×</button>
        </span>)}
        {pendingFiles.length > 0 && <span role="status">{t`Remove these attachments before sending, or send them from Messages.`}</span>}
      </div> : undefined} />
    {commandError && <p role="alert">{i18n._(commandError)}</p>}
  </div></RemoteUIProvider>;

  return (
    <div className={styles.root} data-message-input-root data-disabled={disabled || undefined}>
      <SlashCommandPicker
        groups={slash.filtered}
        visible={slash.isActive}
        selectedIndex={slash.selectedIndex}
        onSelect={handleSlashSelect}
        onClose={slash.close}
      />

      {/* Attachment preview strip */}
      {pendingFiles.length > 0 && (
        <div className={styles.attachmentPreviewStrip}>
          {pendingFiles.map((file, i) => (
            <div
              key={i}
              className={styles.attachmentPreviewItem}
              data-testid="pending-attachment"
              data-attachment-kind={previewUrls[i] ? 'image' : 'file'}
            >
              {previewUrls[i] ? (
                <img src={previewUrls[i]!} alt={file.name} className={styles.attachmentPreviewThumb} />
              ) : (
                <div className={styles.attachmentPreviewFile} aria-hidden="true">📎</div>
              )}
              <button
                className={styles.attachmentPreviewRemove}
                onClick={() => {
                  setPendingFiles((prev) => prev.filter((_, j) => j !== i));
                  setAttachmentError(null);
                }}
                title={t`Remove attachment`}
                aria-label={t`Remove attachment`}
              >
                <CloseCircleFilled aria-hidden="true" />
              </button>
              <span className={styles.attachmentPreviewName}>{file.name}</span>
              <span className={styles.attachmentPreviewMeta}>
                {file.type || t`Unknown type`} · {formatFileSize(i18n, file.size)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inputRow}>
        <button
          className={`icon-btn ${styles.uploadButton}`}
          onClick={() => fileInputRef.current?.click()}
          title={t`Upload files`}
          aria-label={t`Upload files`}
          disabled={disabled}
        >
          <PaperClipOutlined aria-hidden="true" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={MESSAGE_ATTACHMENT_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <div className={`tiptap-wrapper ${styles.tiptapWrapper}`} onKeyDown={handleEditorKeyDown}>
          <EditorContent editor={editor} />
        </div>
        <button
          className={`btn btn-primary ${styles.sendButton}`}
          data-testid="message-send"
          aria-label={t`Send message`}
          onClick={handleSend}
          disabled={
            disabled || (!text.trim() && pendingFiles.length === 0) || sendStatus === 'sending'
          }
        >
          <SendOutlined aria-hidden="true" />
        </button>
      </div>

      {commandError && <div className={`${styles.status} ${styles.statusError}`} data-testid="message-send-status" data-error>{i18n._(commandError)}</div>}
      {attachmentError && <div className={`${styles.status} ${styles.statusError}`} data-testid="attachment-selection-status" data-error>{i18n._(attachmentError)}</div>}
      {statusText() && (
        <div className={`${styles.status} ${sendStatus === 'error' ? styles.statusError : ''}`} data-testid="message-send-status" data-error={sendStatus === 'error' || undefined}>
          {statusText()}
        </div>
      )}
    </div>
  );
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
